/**
 * SPDX-FileCopyrightText: 2019 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import { showError, showSuccess } from '@nextcloud/dialogs'
import { translate as t } from '@nextcloud/l10n'
import { generateUrl } from '@nextcloud/router'
import { useHotKey } from '@nextcloud/vue/composables/useHotKey'
import { mapState, mapStores } from 'pinia'
import { getRFCProperties } from '@/models/rfcProps.js'
import { containsRoomUrl } from '@/services/talkService.ts'
import useCalendarObjectInstanceStore from '@/store/calendarObjectInstance.js'
import useCalendarObjectsStore from '@/store/calendarObjects.js'
import useCalendarsStore from '@/store/calendars.js'
import usePrincipalsStore from '@/store/principals.js'
import useSettingsStore from '@/store/settings.js'
import useWidgetStore from '@/store/widget.js'
import { updateDefaultAlarm } from '@/utils/alarms.js'
import { removeMailtoPrefix } from '@/utils/attendee.js'
import { isBaseOccurrence } from '@/utils/calendarObject.js'
import { uidToHexColor } from '@/utils/color.js'
import { dateFactory } from '@/utils/date.js'
import logger from '@/utils/logger.js'
import { getPrefixedRoute, getViewMode, ViewMode } from '@/utils/router.js'

/**
 * This is a mixin for the editor. It contains common Vue stuff, that is
 * required both in the popover as well as the full page editor.
 *
 * See inline for more documentation
 */
export default {
	props: {
		// Whether or not the calendar is embedded in a widget
		isWidget: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			// Indicator whether or not the event is currently loading, saving or being deleted
			isLoading: true,
			// Indicator whether or not the event is currently saving
			isSaving: false,
			// Indicator whether or not loading the event failed
			isError: false,
			// Error message in case there was an error
			error: null,
			// The calendar-id of the selected calendar
			calendarId: null,
			// Whether or not an action is required on leave
			requiresActionOnRouteLeave: true,
			// Whether changing the recurrence rule requires updating this and future occurrences
			requiresFutureUpdate: false,
			// Whether the primary (first) occurrence of a recurring series is being edited,
			// as opposed to any later occurrence - always true for a brand new event
			isEditingBaseInstance: false,
			// Whether or not a recurrence-exception is being edited
			isEditingExceptionInstance: false,
			// Whether or not the Talk modal is open
			isTalkModalOpen: false,
			// Cleanup functions for hotkeys
			hotKeysRegister: [],
		}
	},
	computed: {
		...mapState(useSettingsStore, {
			currentUserTimezone: 'getResolvedTimezone',
		}),
		...mapState(useSettingsStore, ['talkEnabled']),
		...mapState(useCalendarsStore, ['initialCalendarsLoaded']),
		...mapState(useCalendarObjectInstanceStore, ['calendarObject', 'calendarObjectInstance']),
		...mapStores(useCalendarsStore, usePrincipalsStore, useCalendarObjectsStore, useCalendarObjectInstanceStore, useSettingsStore, useWidgetStore),
		eventComponent() {
			return this.calendarObjectInstance?.eventComponent
		},
		/**
		 * Returns the events title or an empty string if the event is still loading
		 *
		 * @return {string}
		 */
		title() {
			return this.calendarObjectInstance?.title ?? ''
		},
		/**
		 * Returns the location or null if the event is still loading
		 *
		 * @return {string|null}
		 */
		location() {
			return this.calendarObjectInstance?.location ?? null
		},
		/**
		 * Returns the description or null if the event is still loading
		 *
		 * @return {string|null}
		 */
		description() {
			return this.calendarObjectInstance?.description ?? null
		},
		/**
		 * Returns the start-date (without timezone) or null if the event is still loading
		 *
		 * @return {Date|null}
		 */
		startDate() {
			return this.calendarObjectInstance?.startDate ?? null
		},
		/**
		 * Returns the timezone of the event's start-date or null if the event is still loading
		 *
		 * @return {string|null}
		 */
		startTimezone() {
			return this.calendarObjectInstance?.startTimezoneId ?? null
		},
		/**
		 * Returns the end-date (without timezone) or null if the event is still loading
		 *
		 * @return {Date|null}
		 */
		endDate() {
			return this.calendarObjectInstance?.endDate ?? null
		},
		/**
		 * Returns the timezone of the event's end-date or null if the event is still loading
		 *
		 * @return {string|null}
		 */
		endTimezone() {
			return this.calendarObjectInstance?.endTimezoneId ?? null
		},
		/**
		 * Returns whether or not the event is all-day or null if the event is still loading
		 *
		 * @return {boolean}
		 */
		isAllDay() {
			return this.calendarObjectInstance?.isAllDay ?? false
		},
		/**
		 * Returns whether or not the event has been cancelled
		 *
		 * @return {boolean}
		 */
		isCancelled() {
			return this.calendarObjectInstance.status === 'CANCELLED'
		},
		/**
		 * Returns whether or not the user is allowed to modify the all-day setting
		 *
		 * @return {boolean}
		 */
		canModifyAllDay() {
			return (this.calendarObjectInstance?.canModifyAllDay ?? false) || !(this.calendarObject?.existsOnServer ?? true)
		},
		/**
		 * Returns the color the illustration should be colored in
		 *
		 * @return {string}
		 */
		illustrationColor() {
			return this.color || this.selectedCalendarColor
		},
		/**
		 * Returns the color of the calendar selected by the user
		 * This is used to color illustration
		 *
		 * @return {string}
		 */
		selectedCalendarColor() {
			if (!this.selectedCalendar) {
				const calendars = this.calendarsStore.sortedCalendars
				if (calendars.length > 0) {
					return calendars[0].color
				}

				return uidToHexColor('')
			}

			return this.selectedCalendar.color
		},
		/**
		 * Returns the custom color of this event
		 *
		 * @return {null | string}
		 */
		color() {
			return this.calendarObjectInstance?.customColor ?? null
		},
		/**
		 * Returns whether or not to display save buttons
		 *
		 * @return {boolean}
		 */
		showSaveButtons() {
			return this.isReadOnly === false
		},
		/**
		 * Returns whether or not to allow editing the event
		 *
		 * @return {boolean}
		 */
		isReadOnly() {
			if (!this.calendarObject) {
				return true
			}

			const calendar = this.calendarsStore.getCalendarById(this.calendarObject.calendarId)
			if (!calendar) {
				return true
			}

			return !calendar.canCreateObject && !calendar.canModifyObject
		},
		isSharedWithMe() {
			if (!this.calendarObject) {
				return true
			}

			const calendar = this.calendarsStore.getCalendarById(this.calendarObject.calendarId)
			if (!calendar) {
				return true
			}

			return calendar.isSharedWithMe
		},
		/**
		 * Returns whether the user is an attendee of the event
		 *
		 * @return {boolean}
		 */
		isViewedByAttendee() {
			return this.userAsAttendee !== null
		},
		/**
		 * Returns whether the user is the organizer of the event or null if the user can't be an organizer
		 *
		 * @return {boolean|null}
		 */
		isViewedByOrganizer() {
			if (!this.calendarObjectInstance || !this.calendarObjectInstance.attendees.length) {
				return null
			}

			if (this.isReadOnly || !this.principalsStore.getCurrentUserPrincipalEmail || !this.calendarObjectInstance.organizer) {
				return null
			}

			const principal = removeMailtoPrefix(this.principalsStore.getCurrentUserPrincipalEmail)
			const organizer = this.calendarObjectInstance.organizer
			return removeMailtoPrefix(organizer.uri) === principal
		},
		/**
		 * Returns the attendee property corresponding to the current user
		 *
		 * @return {?object}
		 */
		userAsAttendee() {
			if (!this.calendarObjectInstance || this.isReadOnly || !this.principalsStore.getCurrentUserPrincipalEmail || !this.calendarObjectInstance.organizer) {
				return null
			}

			const principal = removeMailtoPrefix(this.principalsStore.getCurrentUserPrincipalEmail)
			for (const attendee of this.calendarObjectInstance.attendees) {
				if (removeMailtoPrefix(attendee.uri) === principal) {
					return attendee
				}
			}

			return null
		},
		/**
		 * Returns all calendars selectable by the user
		 *
		 * @return {object[]}
		 */
		calendars() {
			if (this.isReadOnly && this.calendarObject) {
				return [
					this.calendarsStore.getCalendarById(this.calendarObject.calendarId),
				]
			}

			return this.calendarsStore.sortedCalendars
		},
		/**
		 * Returns the object of the selected calendar
		 *
		 * @return {object}
		 */
		selectedCalendar() {
			return this.calendarsStore.getCalendarById(this.calendarId)
		},
		/**
		 * Returns the userId of the delegator when the selected calendar is delegated, or null otherwise
		 *
		 * @return {string|null}
		 */
		delegatorUserId() {
			if (!this.selectedCalendar?.isDelegated || !this.selectedCalendar.delegatorUrl) {
				return null
			}
			return this.principalsStore.getPrincipalByUrl(this.selectedCalendar.delegatorUrl)?.userId ?? null
		},
		/**
		 * Returns the mode the editor is currently rendered in
		 * (authenticated user, public share, embedded share, or widget).
		 *
		 * @return {string} One of ViewMode
		 */
		viewMode() {
			return getViewMode(this.$route?.name, this.isWidget)
		},
		/**
		 * @return {boolean}
		 */
		canDuplicate() {
			return this.viewMode === ViewMode.USER
		},
		/**
		 * Returns whether the current event is a recurring instance or exception.
		 *
		 * @return {boolean}
		 */
		isRecurringInstance() {
			return this.canCreateRecurrenceException || this.isEditingExceptionInstance
		},
		/**
		 * Returns whether or not the user is allowed to create recurrence exceptions for this event
		 *
		 * @return {boolean}
		 */
		canCreateRecurrenceException() {
			if (!this.eventComponent) {
				return false
			}

			return this.eventComponent.canCreateRecurrenceExceptions()
		},
		/**
		 * Returns whether the calendar of the event can be modified
		 *
		 * @return {boolean}
		 */
		canModifyCalendar() {
			if (!this.calendarObjectInstance) {
				return true
			}
			const eventComponent = this.calendarObjectInstance.eventComponent
			if (!eventComponent) {
				return true
			}

			return !eventComponent.isPartOfRecurrenceSet() || eventComponent.isExactForkOfPrimary
		},
		/**
		 * Returns a an object with properties from RFCs including
		 * their displayName, a description, options, etc.
		 *
		 * @return {{geo, color, timeTransparency, description, resources, location, categories, accessClass, priority, status}}
		 */
		rfcProps() {
			return getRFCProperties()
		},
		/**
		 * Returns whether or not this event can be downloaded from the server
		 *
		 * @return {boolean}
		 */
		hasDownloadURL() {
			if (!this.calendarObject) {
				return false
			}
			if (this.isLoading) {
				return false
			}

			return this.calendarObject?.existsOnServer ?? false
		},
		/**
		 * Returns the download url as a string or null if event is loading or does not exist on the server (yet)
		 *
		 * @return {string|null}
		 */
		downloadURL() {
			if (!this.calendarObject) {
				return null
			}

			if (!this.calendarObject.dav) {
				return null
			}

			return this.calendarObject.dav.url + '?export'
		},
		/**
		 * Returns the permanent deep link URL for this event, or null if the event is new
		 *
		 * @return {string|null}
		 */
		eventLink() {
			if (!this.calendarObject) {
				return null
			}

			const uid = this.calendarObject.uid
			if (!uid) {
				return null
			}

			const recurrenceId = this.$route?.params?.recurrenceId
			if (recurrenceId && recurrenceId !== 'next') {
				return window.location.origin + generateUrl('/apps/calendar/object/{uid}/{recurrenceId}', { uid, recurrenceId })
			}

			return window.location.origin + generateUrl('/apps/calendar/object/{uid}', { uid })
		},
		/**
		 * Returns whether or not this is a new event
		 *
		 * @return {boolean}
		 */
		isNew() {
			if (!this.calendarObject) {
				return true
			}

			if (!this.calendarObject.dav) {
				return true
			}

			return false
		},

		/**
		 * Returns whether the Talk room button should be disabled
		 * (i.e. location or description already contains a Talk room URL)
		 *
		 * @return {boolean}
		 */
		isCreateTalkRoomButtonDisabled() {
			return containsRoomUrl(this.calendarObjectInstance?.location) || containsRoomUrl(this.calendarObjectInstance?.description)
		},

		/**
		 * Returns whether the Talk room button should be visible
		 *
		 * @return {boolean}
		 */
		isCreateTalkRoomButtonVisible() {
			return this.talkEnabled && this.isViewedByOrganizer !== false && this.isReadOnly !== true
		},
	},

	async created() {
		// Skip data loading for widgets - they handle it in mounted()
		if (this.isWidget) {
			return
		}

		// Check if this is a new event or existing event based on route name
		// NewPopoverView and NewFullView are for new events
		const isNewEvent = this.$route?.name?.startsWith('New')

		if (isNewEvent) {
			// For new events, create a new calendar object instance
			logger.debug('[Editor] Creating new event')
			try {
				await this.loadingCalendars()

				const isAllDay = (this.$route.params.allDay === '1')
				const start = parseInt(this.$route.params.dtstart)
				const end = parseInt(this.$route.params.dtend)
				const timezoneId = this.settingsStore.getResolvedTimezone

				await this.calendarObjectInstanceStore.getCalendarObjectInstanceForNewEvent({
					isAllDay,
					start,
					end,
					timezoneId,
				})

				// Set the calendarId from the created calendar object
				if (this.calendarObject) {
					this.calendarId = this.calendarObject.calendarId
					this.addDelegatorAsAttendeeIfNeeded(this.selectedCalendar)
				}

				// A new event has no recurrence-id, so it is always its own base item
				this.isEditingBaseInstance = true

				logger.debug('[Editor] New event created successfully')
			} catch (error) {
				logger.error('[Editor] Error creating new event:', { error })
			} finally {
				this.isLoading = false
			}
		} else {
			// For existing events, load the event data
			const objectId = this.$route.params.object
			const recurrenceId = this.$route.params.recurrenceId

			logger.debug('[Editor] Loading event data...', { objectId, recurrenceId })

			try {
				await this.loadingCalendars()
				await this.calendarObjectInstanceStore.getCalendarObjectInstanceByObjectIdAndRecurrenceId({ objectId, recurrenceId })
				this.calendarId = this.calendarObject.calendarId
				this.isEditingBaseInstance = isBaseOccurrence(this.calendarObject, this.eventComponent)
				this.isEditingExceptionInstance = this.eventComponent.isRecurrenceException()
				logger.debug('[Editor] Event loaded successfully')
			} catch (error) {
				logger.error('[Editor] Error loading event:', { error })
				this.isError = true
				this.error = this.$t('calendar', 'It might have been deleted, or there was a typo in a link')
			} finally {
				this.isLoading = false
				logger.debug('[Editor] isLoading set to false')
			}
		}
	},

	mounted() {
		this.hotKeysRegister = [
			useHotKey('Escape', () => this.keyboardCloseEditor(), { allowInModal: true }),
			useHotKey('Enter', () => this.keyboardSaveEvent(), { ctrl: true, allowInModal: true, allowInInput: true }),
			useHotKey('Delete', () => this.keyboardDeleteEvent(), { ctrl: true, allowInModal: true }),
			useHotKey('d', () => this.keyboardDuplicateEvent(), { ctrl: true, prevent: true, allowInModal: true }),
		]
	},

	beforeUnmount() {
		this.hotKeysRegister.forEach((stop) => stop())
	},

	methods: {
		/**
		 * Opens the Talk modal for selecting or creating a Talk room
		 */
		openTalkModal() {
			this.isTalkModalOpen = true
		},

		/**
		 * Changes the selected calendar
		 * Does not move the calendar-object yet, that's done in save
		 *
		 * @param {object} selectedCalendar The new calendar selected by the user
		 */
		changeCalendar(selectedCalendar) {
			this.calendarId = selectedCalendar.id

			// If this is a new event that does not exist on the server yet,
			// override the internally stored calendarId. If we did not do this,
			// it would create the event in the default calendar first and move it
			// to the desired calendar as a second step.
			if (this.calendarObject && !this.calendarObject.existsOnServer) {
				this.calendarObject.calendarId = selectedCalendar.id
				this.addDelegatorAsAttendeeIfNeeded(selectedCalendar)
			}

			updateDefaultAlarm(this.calendarObject.calendarId, this.calendarObjectInstance)
		},

		/**
		 * When creating an event on a delegated calendar, sets the delegator as the organizer.
		 * The assistant (current user) should not be listed as organizer or attendee.
		 *
		 * @param {object|null} calendar The calendar object to check
		 */
		addDelegatorAsAttendeeIfNeeded(calendar) {
			if (!calendar?.isDelegated || !calendar.delegatorUrl) {
				return
			}

			if (!this.calendarObjectInstance) {
				return
			}

			const delegatorPrincipal = this.principalsStore.getPrincipalByUrl(calendar.delegatorUrl)
			if (!delegatorPrincipal?.emailAddress) {
				return
			}

			if (!this.calendarObjectInstance.organizer) {
				this.calendarObjectInstanceStore.setOrganizer({
					commonName: delegatorPrincipal.displayname,
					email: delegatorPrincipal.emailAddress,
				})
			}
		},
		/**
		 * Require recurrence changes to apply to this and future occurrences.
		 */
		requireFutureUpdate() {
			this.requiresFutureUpdate = true
		},
		/**
		 * Closes the editor and returns to normal calendar-view
		 */
		closeEditor() {
			if (this.isWidget) {
				this.widgetStore.closeWidgetEventDetails()
				return
			}
			const params = { ...this.$route.params }
			delete params.object
			delete params.recurrenceId

			const targetRouteName = getPrefixedRoute(this.$route?.name ?? 'CalendarView', 'CalendarView')

			this.$router.push({
				name: targetRouteName,
				params,
			})

			this.calendarObjectInstanceStore.resetCalendarObjectInstanceObjectIdAndRecurrenceId()
		},
		/**
		 * Closes the editor and returns to normal calendar-view without running any action.
		 * This is useful if the calendar-object-instance has already been saved.
		 */
		closeEditorAndSkipAction() {
			this.requiresActionOnRouteLeave = false
			this.closeEditor()
		},
		/**
		 * Close cancel dialog
		 */
		closeCancelDialog() {
			this.showCancelDialog = false
		},
		/**
		 * Resets the calendar-object back to its original state and closes the editor
		 *
		 * @param {boolean} force whether to not show a confirmation modal before executing
		 */
		async cancel(force = false) {
			if (this.isLoading) {
				return
			}

			if (!this.calendarObject) {
				logger.error('Calendar-object not found')
				this.closeEditor()
				return
			}

			if (this.calendarObjectInstanceStore.calendarObjectInstance.eventComponent.isDirty() && !force) {
				this.showCancelDialog = true
			} else {
				this.calendarObjectsStore.resetCalendarObjectToDavMutation({
					calendarObject: this.calendarObject,
				})

				this.requiresActionOnRouteLeave = false
				this.closeEditor()
			}
		},
		keyboardCloseEditor() {
			this.cancel(false)
		},
		keyboardSaveEvent() {
			if (!this.isRecurringInstance && this.canUpdate('occurrence')) {
				this.saveAndLeave('occurrence')
			}
		},
		keyboardDeleteEvent() {
			if (!this.isRecurringInstance && this.canDelete('occurrence')) {
				this.deleteAndLeave('occurrence')
			}
		},
		keyboardDuplicateEvent() {
			if (!this.isNew && this.canDuplicate) {
				this.duplicateEvent()
			}
		},
		/**
		 * Saves a calendar-object
		 *
		 * @param {string} scope Modification scope: 'occurrence', 'future', or 'series'
		 * @return {Promise<void>}
		 */
		async save(scope = 'occurrence') {
			if (!this.calendarObject) {
				logger.error('Calendar-object not found')
				return
			}
			if (this.requiresFutureUpdate) {
				scope = 'future'
			}
			if (!this.canUpdate(scope)) {
				return
			}

			this.isLoading = true
			this.isSaving = true
			try {
				await this.calendarObjectInstanceStore.saveCalendarObjectInstance({
					scope,
					calendarId: this.calendarId,
				})
			} catch (error) {
				logger.error(`Failed to save event: ${error}`, {
					error,
				})
				showError(t('calendar', 'Failed to save event'))
				this.calendarObjectInstance.eventComponent.markDirty()
				throw error
			} finally {
				this.isLoading = false
				this.isSaving = false
			}
		},

		/**
		 * Returns whether the current user can update the event with the given scope.
		 *
		 * @param {string} scope Modification scope: 'occurrence', 'future', or 'series'
		 * @return {boolean}
		 */
		canUpdate(scope) {
			if (!this.calendarObject || this.isReadOnly || this.isLoading) {
				return false
			}
			if (this.isNew) {
				return scope === 'occurrence'
			}
			if (!this.calendarObject.existsOnServer) {
				return false
			}
			if (this.requiresFutureUpdate) {
				return scope === 'future'
			}
			if (!this.isRecurringInstance) {
				return scope === 'occurrence'
			}
			if ((scope === 'series' || scope === 'future') && this.isEditingExceptionInstance) {
				return false
			}
			if (this.isViewedByAttendee) {
				return scope === 'series' || (this.isEditingExceptionInstance && scope === 'occurrence')
			}
			if (!this.isEditingExceptionInstance && this.isEditingBaseInstance && scope !== 'series') {
				return false
			}

			return ['occurrence', 'future', 'series'].includes(scope)
		},

		/**
		 * Saves a calendar-object and closes the editor
		 *
		 * @param {string} scope Modification scope: 'occurrence', 'future', or 'series'
		 * @return {Promise<void>}
		 */
		async saveAndLeave(scope = 'occurrence') {
			await this.save(scope)
			this.requiresActionOnRouteLeave = false
			this.closeEditor()
		},

		/**
		 * Duplicates the calendar-object. If the source calendar is
		 * read-only, the duplicate is created in the first writable calendar.
		 *
		 * @return {Promise<void>}
		 */
		async duplicateEvent() {
			if (!this.canDuplicate) {
				return
			}

			const calendarId = this.isReadOnly
				? (this.calendarsStore.sortedCalendars[0]?.id ?? null)
				: (this.calendarObject?.calendarId ?? null)
			await this.calendarObjectInstanceStore.duplicateCalendarObjectInstance({ calendarId })

			// The editor's calendar picker is driven by this.calendarId, which is
			// separate from the store's calendarObject.calendarId.
			this.calendarId = this.calendarObject?.calendarId ?? null
		},

		/**
		 * Copies the permanent event deep link to the clipboard
		 *
		 * @return {Promise<void>}
		 */
		async copyEventLink() {
			if (!this.eventLink) {
				return
			}

			try {
				await navigator.clipboard.writeText(this.eventLink)
				showSuccess(t('calendar', 'Event link copied to clipboard'))
			} catch (error) {
				logger.error('Failed to copy event link to clipboard', { error })
				showError(t('calendar', 'Failed to copy event link'))
			}
		},

		/**
		 * Returns whether the current user can delete the event with the given scope.
		 *
		 * @param {string} scope Deletion scope: 'occurrence', 'future', or 'series'
		 * @return {boolean}
		 */
		canDelete(scope) {
			if (!this.calendarObject || this.isReadOnly || this.isLoading || !this.calendarObject.existsOnServer) {
				return false
			}
			if (!this.isRecurringInstance) {
				return scope === 'occurrence'
			}
			if ((scope === 'series' || scope === 'future') && this.isEditingExceptionInstance) {
				return false
			}
			if (this.isViewedByAttendee) {
				return scope === 'series' || (this.isEditingExceptionInstance && scope === 'occurrence')
			}
			if (!this.isEditingExceptionInstance && this.isEditingBaseInstance && scope !== 'series') {
				return false
			}

			return ['occurrence', 'future', 'series'].includes(scope)
		},

		/**
		 * Deletes a calendar-object
		 *
		 * @param {string} scope Deletion scope: 'occurrence', 'future', or 'series'
		 * @return {Promise<void>}
		 */
		async delete(scope = 'occurrence') {
			if (!this.calendarObject) {
				logger.error('Calendar-object not found')
				return
			}
			if (!this.canDelete(scope)) {
				return
			}

			this.isLoading = true
			await this.calendarObjectInstanceStore.deleteCalendarObjectInstance({ scope })
			this.isLoading = false
		},

		/**
		 * Deletes a calendar-object and closes the editor
		 *
		 * @param {string} scope Deletion scope: 'occurrence', 'future', or 'series'
		 * @return {Promise<void>}
		 */
		async deleteAndLeave(scope = 'occurrence') {
			await this.delete(scope)
			this.requiresActionOnRouteLeave = false
			this.closeEditor()
		},

		/**
		 * Updates the title of this event
		 *
		 * @param {string} title New title
		 */
		updateTitle(title) {
			if (title.trim() === '') {
				title = null
			}

			this.calendarObjectInstanceStore.changeTitle({
				title,
			})
		},
		/**
		 * Updates the description of this event
		 *
		 * @param {string} description New description
		 */
		updateDescription(description) {
			this.calendarObjectInstanceStore.changeDescription({
				description,
			})
		},
		/**
		 * Updates the location of this event
		 *
		 * @param {string} location New location
		 */
		updateLocation(location) {
			this.calendarObjectInstanceStore.changeLocation({
				location,
			})
		},
		/**
		 * Updates the start date of this event
		 *
		 * @param {Date} startDate New start date
		 */
		updateStartDate(startDate) {
			const combinedStartDate = new Date(startDate)
			combinedStartDate.setHours(
				this.calendarObjectInstance.startDate.getHours(),
				this.calendarObjectInstance.startDate.getMinutes(),
				this.calendarObjectInstance.startDate.getSeconds(),
				0,
			)

			this.calendarObjectInstanceStore.changeStartDate({
				startDate: combinedStartDate,
				onlyTime: false,
				changeEndDate: true,
			})
		},
		/**
		 * Updates the start time of this event
		 *
		 * @param {Date} startDate New start time
		 */
		updateStartTime(startDate) {
			this.calendarObjectInstanceStore.changeStartDate({
				startDate,
				onlyTime: true,
				changeEndDate: true,
			})
		},
		/**
		 * Updates the timezone of this event's start date
		 *
		 * @param {string} startTimezone New start timezone
		 */
		updateStartTimezone(startTimezone) {
			if (!startTimezone) {
				return
			}

			this.calendarObjectInstanceStore.changeStartTimezone({
				startTimezone,
			})
		},
		/**
		 * Updates the end date of this event
		 *
		 * @param {Date} endDate New end date
		 */
		updateEndDate(endDate) {
			const combinedEndDate = new Date(endDate)
			combinedEndDate.setHours(
				this.calendarObjectInstance.endDate.getHours(),
				this.calendarObjectInstance.endDate.getMinutes(),
				this.calendarObjectInstance.endDate.getSeconds(),
				0,
			)

			this.calendarObjectInstanceStore.changeEndDate({
				endDate: combinedEndDate,
			})
		},
		/**
		 * Updates the end time of this event
		 *
		 * @param {Date} endDate New end date
		 */
		updateEndTime(endDate) {
			this.calendarObjectInstanceStore.changeEndDate({
				endDate,
				onlyTime: true,
			})
		},
		/**
		 * Updates the timezone of this event's end date
		 *
		 * @param {string} endTimezone New end timezone
		 */
		updateEndTimezone(endTimezone) {
			if (!endTimezone) {
				return
			}

			this.calendarObjectInstanceStore.changeEndTimezone({
				endTimezone,
			})
		},
		/**
		 * Toggles the event between all-day and timed
		 */
		toggleAllDay() {
			this.calendarObjectInstanceStore.toggleAllDay()

			updateDefaultAlarm(this.calendarObject.calendarId, this.calendarObjectInstance)
		},
		/**
		 * Resets the internal state after changing the viewed calendar-object
		 */
		resetState() {
			this.isLoading = true
			this.isSaving = false
			this.isError = false
			this.error = null
			this.calendarId = null
			this.requiresActionOnRouteLeave = true
			this.requiresFutureUpdate = false
			this.isEditingBaseInstance = false
			this.isEditingExceptionInstance = false
		},
		/**
		 * This function returns a promise that resolves
		 * once the calendars were fetched from the server
		 *
		 * @return {Promise<void>}
		 */
		loadingCalendars() {
			if (this.initialCalendarsLoaded) {
				return Promise.resolve()
			}

			return new Promise((resolve) => {
				const watcher = this.$watch('initialCalendarsLoaded', () => {
					resolve()
					watcher()
				})
			})
		},
	},
	/**
	 * This is executed before entering the Editor routes
	 *
	 * @param {object} to The route to navigate to
	 * @param {object} from The route coming from
	 * @param {(vm?: object) => void} next Function to be called when ready to load the next view
	 */
	async beforeRouteEnter(to, from, next) {
		if (to.name === 'NewFullView' || to.name === 'NewPopoverView') {
			next(async (vm) => {
				vm.resetState()

				const isAllDay = (to.params.allDay === '1')
				const start = parseInt(to.params.dtstart, 10)
				const end = parseInt(to.params.dtend, 10)
				const timezoneId = vm.settingsStore.getResolvedTimezone

				try {
					await vm.loadingCalendars()
					await vm.calendarObjectInstanceStore.getCalendarObjectInstanceForNewEvent({ isAllDay, start, end, timezoneId })
					vm.calendarId = vm.calendarObject.calendarId
				} catch (error) {
					logger.debug(error)
					vm.isError = true
					vm.error = t('calendar', 'It might have been deleted, or there was a typo in a link')
				} finally {
					vm.isLoading = false
				}
			})
		} else {
			next(async (vm) => {
				vm.resetState()
				const objectId = to.params.object
				const recurrenceId = to.params.recurrenceId

				if (recurrenceId === 'next') {
					const closeToDate = dateFactory()
					// TODO: can we replace this by simply returning the new route since we are inside next()
					// Probably not though, because it's async
					try {
						await vm.loadingCalendars()
						const recurrenceId = await vm.calendarObjectInstanceStore.resolveClosestRecurrenceIdForCalendarObject({
							objectId,
							closeToDate,
						})
						const params = { ...vm.$route.params, recurrenceId }
						vm.$router.replace({ name: vm.$route.name, params })
					} catch (error) {
						logger.debug(error)
						vm.isError = true
						vm.error = t('calendar', 'It might have been deleted, or there was a typo in a link')
						return // if we cannot resolve next to an actual recurrenceId, return here to avoid further processing.
					} finally {
						vm.isLoading = false
					}
				}

				try {
					await vm.loadingCalendars()
					await vm.calendarObjectInstanceStore.getCalendarObjectInstanceByObjectIdAndRecurrenceId({ objectId, recurrenceId })
					vm.calendarId = vm.calendarObject.calendarId
					vm.isEditingBaseInstance = isBaseOccurrence(vm.calendarObject, vm.eventComponent)
					vm.isEditingExceptionInstance = vm.eventComponent.isRecurrenceException()
				} catch (error) {
					logger.debug(error)
					vm.isError = true
					vm.error = t('calendar', 'It might have been deleted, or there was a typo in a link')
				} finally {
					vm.isLoading = false
				}
			})
		}
	},
	/**
	 * This function is called when the route changes. This can be caused by various actions:
	 * - Change of selected time-range when creating new event
	 * - Navigating through the calendar-view
	 *
	 * @param {object} to The route to navigate to
	 * @param {object} from The route coming from
	 * @param {(vm?: object) => void} next Function to be called when ready to load the next view
	 */
	async beforeRouteUpdate(to, from, next) {
		// If we are in the New Event dialog, we want to update the selected time
		if (to.name === 'NewFullView' || to.name === 'NewPopoverView') {
			// If allDay, dtstart and dtend are the same there is no need to update.
			// This is usally the case when navigating through the calendar while the editor is open
			if (to.params.allDay === from.params.allDay
				&& to.params.dtstart === from.params.dtstart
				&& to.params.dtend === from.params.dtend) {
				next()
				return
			}

			const isAllDay = (to.params.allDay === '1')
			const start = to.params.dtstart
			const end = to.params.dtend
			const timezoneId = this.settingsStore.getResolvedTimezone

			await this.loadingCalendars()
			this.calendarObjectInstanceStore.updateCalendarObjectInstanceForNewEvent({ isAllDay, start, end, timezoneId })
			next()
		} else {
			// If both the objectId and recurrenceId remained the same
			// there is no need to update. This is usally the case when navigating
			// through the calendar while the editor is open
			if (to.params.object === from.params.object
				&& to.params.recurrenceId === from.params.recurrenceId) {
				next()
				return
			}

			this.isLoading = true

			try {
				await this.save()
			} catch (error) {
				logger.debug(error)
				next(false)
				return
			}

			this.resetState()
			const objectId = to.params.object
			const recurrenceId = to.params.recurrenceId
			if (recurrenceId === 'next') {
				const closeToDate = dateFactory()
				await this.loadingCalendars()
				const recurrenceId = await this.calendarObjectInstanceStore.resolveClosestRecurrenceIdForCalendarObject({
					objectId,
					closeToDate,
				})
				const params = { ...this.$route.params, recurrenceId }
				next({ name: this.$route.name, params })
				return
			}

			try {
				await this.loadingCalendars()
				await this.calendarObjectInstanceStore.getCalendarObjectInstanceByObjectIdAndRecurrenceId({ objectId, recurrenceId })
				this.calendarId = this.calendarObject.calendarId
				this.isEditingBaseInstance = isBaseOccurrence(this.calendarObject, this.eventComponent)
				this.isEditingExceptionInstance = this.eventComponent.isRecurrenceException()
			} catch (error) {
				logger.debug(error)
				this.isError = true
				this.error = t('calendar', 'It might have been deleted, or there was a typo in the link')
			} finally {
				this.isLoading = false
				next()
			}
		}
	},
	/**
	 * This route is called when the user leaves the editor
	 *
	 * @param {object} to The route to navigate to
	 * @param {object} from The route coming from
	 * @param {(vm?: object) => void} next Function to be called when ready to load the next view
	 */
	async beforeRouteLeave(to, from, next) {
		// requiresActionOnRouteLeave is false when an action like deleting / saving / cancelling was already taken.
		// The responsibility of this method is to automatically save the event when the user clicks outside the editor
		if (!this.requiresActionOnRouteLeave) {
			next()
			return
		}

		try {
			if ((from.name !== 'NewPopoverView' || to.name !== 'EditPopoverView')
				&& (from.name !== 'NewPopoverView' || to.name !== 'EditFullView')) {
				await this.save()
			}
			next()
		} catch (error) {
			logger.debug(error)
			next(false)
		}
	},
}
